/**
 * Hosted Agent Deletion Store
 *
 * Browser-session-scoped Zustand store that tracks hosted agents which were successfully
 * deleted in AI Foundry but whose LOCAL cleanup (removing the entry from
 * connections.json / connections-draft.json) ultimately failed after retries.
 *
 * Why this exists: when local cleanup fails, the agent remains in connections.json
 * with its old (often healthy) status, so every read surface — the Agents list,
 * the Agent detail page, and the workflow designer — would otherwise show the agent
 * as perfectly fine even though it is gone in Foundry and cannot run. This store is
 * the single source of truth those surfaces read to render a durable error and offer
 * a retry (deletion is idempotent: a retry hits Foundry 404 and finishes local cleanup).
 *
 * Failures are persisted in sessionStorage so a same-tab reload retains the retry
 * guidance. They cannot be stored in connections.json because failure to write that
 * file is exactly the condition being recorded. BroadcastChannel synchronizes other
 * open tabs, while only sanitized messages are persisted.
 */

import { create } from 'zustand';
import {
  HOSTED_AGENT_CHANNEL_NAME,
  postHostedAgentCleanupCleared,
  postHostedAgentCleanupFailed,
  postHostedAgentCleanupRecreated,
  type HostedAgentChannelMessage,
  type HostedAgentCleanupFailurePayload,
  type HostedAgentCleanupSyncRecord,
} from '@/services/hostedAgentChannel';
import { sanitizeErrorMessage } from '@/utils/sanitization/errorMessage';

/** A recorded "deleted in Foundry, local cleanup failed" state for one agent. */
export interface HostedAgentCleanupFailure extends HostedAgentCleanupFailurePayload {
  /** Foundry agent name, for display/telemetry. */
  agentName: string;
  /** Human-readable failure detail shown to the user. */
  message: string;
  /** Epoch ms when the failure was recorded, so the UI can show recency if needed. */
  at: number;
}

interface HostedAgentDeletionState {
  /**
   * Cleanup failures keyed by an APP-SCOPED key (see `hostedAgentDeletionKey`).
   * A hosted-agent connection key (the agent name) is only unique within a single
   * app's connections.json, but this store is global and survives navigation
   * between apps — so entries MUST be namespaced by the owning app to avoid a
   * failure for `weather-agent` in app A marking/clearing an unrelated
   * `weather-agent` in app B.
   */
  cleanupFailures: Record<string, HostedAgentCleanupFailure>;
  /**
   * Successful cleanup tombstones keyed by the same app-scoped key.
   * Receiving tabs keep stale cached connection records hidden and block
   * background status writes until a fresh production read confirms absence.
   */
  completedDeletions: Record<string, number>;
  /** Same-key saves awaiting a fresh canonical read in receiving tabs. */
  pendingRecreations: Record<string, number>;
  /** True after the initial cross-tab snapshot window has completed. */
  isSynchronizationReady: boolean;
}

/**
 * Build the app-scoped store key for a hosted-agent cleanup-failure entry.
 * `appId` should be a stable per-app identifier (the app resource id); when it is
 * unavailable we fall back to a literal so keys stay deterministic. Every reader
 * and writer MUST go through this helper so marks and clears line up.
 */
export const hostedAgentDeletionKey = (
  appId: string | null | undefined,
  connectionKey: string,
): string => `${appId ?? 'unknown-app'}::${connectionKey}`;

interface HostedAgentDeletionActions {
  /** Record that an agent was deleted in Foundry but local cleanup failed. */
  markCleanupFailed: (key: string, failure: HostedAgentCleanupFailure) => void;
  /** Clear a previously-recorded failure (e.g. a retry finished cleanup). */
  clearCleanupFailure: (key: string) => void;
  /** Clear deletion state because a new agent was intentionally saved under this key. */
  markAgentRecreated: (key: string) => void;
  /** Remove a recreation guard after a fresh canonical read contains the saved key. */
  acknowledgeAgentRecreated: (key: string, expectedAt?: number) => void;
  /** Remove a completion tombstone after canonical state has been reconciled. */
  acknowledgeCompletedDeletion: (key: string, expectedAt?: number) => void;
}

type HostedAgentDeletionStore = HostedAgentDeletionState & HostedAgentDeletionActions;

const CLEANUP_FAILURE_STORAGE_KEY = 'otto-hosted-agent-cleanup-failures-v1';

function readPersistedCleanupFailures(): Record<string, HostedAgentCleanupFailure> {
  try {
    const storage = window.sessionStorage;
    const parsed: unknown = JSON.parse(storage.getItem(CLEANUP_FAILURE_STORAGE_KEY) ?? '{}');
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};

    const failures: Record<string, HostedAgentCleanupFailure> = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (!value || typeof value !== 'object' || Array.isArray(value)) continue;
      const candidate = value as Partial<HostedAgentCleanupFailure>;
      if (
        typeof candidate.agentName !== 'string'
        || typeof candidate.message !== 'string'
        || typeof candidate.at !== 'number'
        || !Number.isFinite(candidate.at)
      ) {
        continue;
      }
      failures[key] = {
        agentName: candidate.agentName,
        message: sanitizeErrorMessage(candidate.message),
        at: candidate.at,
      };
    }
    return failures;
  } catch {
    return {};
  }
}

function persistCleanupFailures(
  failures: Record<string, HostedAgentCleanupFailure>,
): void {
  try {
    const storage = window.sessionStorage;
    if (Object.keys(failures).length === 0) {
      storage.removeItem(CLEANUP_FAILURE_STORAGE_KEY);
      return;
    }
    storage.setItem(CLEANUP_FAILURE_STORAGE_KEY, JSON.stringify(failures));
  } catch {
    // Storage may be unavailable; in-memory and cross-tab state still work.
  }
}

const initialCleanupFailures = readPersistedCleanupFailures();
const cleanupSyncRecords = new Map<string, HostedAgentCleanupSyncRecord>(
  Object.entries(initialCleanupFailures).map(([key, failure]) => [
    key,
    { at: failure.at, failure },
  ]),
);

export const useHostedAgentDeletionStore = create<HostedAgentDeletionStore>()(() => ({
  cleanupFailures: initialCleanupFailures,
  completedDeletions: {},
  pendingRecreations: {},
  isSynchronizationReady: typeof BroadcastChannel === 'undefined',

  markCleanupFailed: (key, failure) => {
    const sanitizedFailure = {
      ...failure,
      message: sanitizeErrorMessage(failure.message),
    };
    applyHostedAgentCleanupFailed(key, sanitizedFailure);
    postHostedAgentCleanupFailed(key, sanitizedFailure);
  },

  clearCleanupFailure: (key) => {
    const at = Date.now();
    applyHostedAgentCleanupCleared(key, at);
    // Always broadcast a successful cleanup. This tab may have missed the
    // original failure while suspended, while another tab still shows it.
    postHostedAgentCleanupCleared(key, at);
  },

  markAgentRecreated: (key) => {
    const at = Date.now();
    applyHostedAgentCleanupRecreated(key, at);
    postHostedAgentCleanupRecreated(key, at);
  },

  acknowledgeAgentRecreated: (key, expectedAt) => {
    useHostedAgentDeletionStore.setState((state) => {
      const current = state.pendingRecreations[key];
      if (current === undefined || (expectedAt !== undefined && current !== expectedAt)) {
        return state;
      }
      const pendingRecreations = { ...state.pendingRecreations };
      delete pendingRecreations[key];
      return { pendingRecreations };
    });
  },

  acknowledgeCompletedDeletion: (key, expectedAt) => {
    useHostedAgentDeletionStore.setState((state) => {
      const current = state.completedDeletions[key];
      if (current === undefined || (expectedAt !== undefined && current !== expectedAt)) {
        return state;
      }
      const next = { ...state.completedDeletions };
      delete next[key];
      return { completedDeletions: next };
    });
  },
}));

export const __internalsForTesting = {
  resetSyncRecords: (): void => cleanupSyncRecords.clear(),
  readPersistedCleanupFailures,
  storageKey: CLEANUP_FAILURE_STORAGE_KEY,
};

function applyHostedAgentCleanupRecord(
  key: string,
  record: HostedAgentCleanupSyncRecord,
): void {
  const current = cleanupSyncRecords.get(key);
  const recordRank = (value: HostedAgentCleanupSyncRecord): number => (
    value.recreated ? 0 : value.failure ? 1 : 2
  );
  if (
    current
    && (
      current.at > record.at
      || (
        current.at === record.at
        && recordRank(current) >= recordRank(record)
      )
    )
  ) {
    return;
  }

  cleanupSyncRecords.set(key, record);
  if (record.recreated) {
    useHostedAgentDeletionStore.setState((state) => {
      const cleanupFailures = { ...state.cleanupFailures };
      const completedDeletions = { ...state.completedDeletions };
      const pendingRecreations = {
        ...state.pendingRecreations,
        [key]: record.at,
      };
      delete cleanupFailures[key];
      delete completedDeletions[key];
      persistCleanupFailures(cleanupFailures);
      return { cleanupFailures, completedDeletions, pendingRecreations };
    });
    return;
  }

  const failure = record.failure;
  if (failure) {
    useHostedAgentDeletionStore.setState((state) => {
      const completedDeletions = { ...state.completedDeletions };
      const pendingRecreations = { ...state.pendingRecreations };
      delete completedDeletions[key];
      delete pendingRecreations[key];
      const cleanupFailures = {
        ...state.cleanupFailures,
        [key]: {
          ...failure,
          message: sanitizeErrorMessage(failure.message),
        },
      };
      persistCleanupFailures(cleanupFailures);
      return {
        cleanupFailures,
        completedDeletions,
        pendingRecreations,
      };
    });
    return;
  }

  useHostedAgentDeletionStore.setState((state) => {
    const cleanupFailures = { ...state.cleanupFailures };
    const pendingRecreations = { ...state.pendingRecreations };
    delete cleanupFailures[key];
    delete pendingRecreations[key];
    persistCleanupFailures(cleanupFailures);
    return {
      cleanupFailures,
      completedDeletions: {
        ...state.completedDeletions,
        [key]: record.at,
      },
      pendingRecreations,
    };
  });
}

export function applyHostedAgentCleanupFailed(
  key: string,
  failure: HostedAgentCleanupFailure,
): void {
  applyHostedAgentCleanupRecord(key, {
    at: failure.at,
    failure,
  });
}

export function applyHostedAgentCleanupCleared(key: string, at = Date.now()): void {
  applyHostedAgentCleanupRecord(key, { at });
}

export function applyHostedAgentCleanupRecreated(key: string, at = Date.now()): void {
  applyHostedAgentCleanupRecord(key, { at, recreated: true });
}

export function applyHostedAgentCleanupSnapshot(
  records: Record<string, HostedAgentCleanupSyncRecord>,
): void {
  for (const [key, record] of Object.entries(records)) {
    applyHostedAgentCleanupRecord(key, record);
  }
}

function getHostedAgentCleanupSnapshot(): Record<string, HostedAgentCleanupSyncRecord> {
  return Object.fromEntries(cleanupSyncRecords);
}

let deletionChannel: BroadcastChannel | null = null;
let pendingSnapshotRequestId: string | null = null;
let snapshotReadyTimer: ReturnType<typeof setTimeout> | null = null;
if (typeof window !== 'undefined' && typeof BroadcastChannel !== 'undefined') {
  const markSynchronizationReady = () => {
    if (snapshotReadyTimer) clearTimeout(snapshotReadyTimer);
    snapshotReadyTimer = null;
    useHostedAgentDeletionStore.setState({ isSynchronizationReady: true });
  };
  const scheduleSynchronizationReady = () => {
    if (snapshotReadyTimer) clearTimeout(snapshotReadyTimer);
    // Each open tab may respond independently. Wait for a quiet collection
    // window so a fast empty response cannot outrun a slower failure record.
    snapshotReadyTimer = setTimeout(markSynchronizationReady, 500);
  };
  const openChannel = () => {
    if (deletionChannel) return;
    useHostedAgentDeletionStore.setState({ isSynchronizationReady: false });
    try {
      deletionChannel = new BroadcastChannel(HOSTED_AGENT_CHANNEL_NAME);
      deletionChannel.onmessage = (event: MessageEvent<HostedAgentChannelMessage>) => {
        if (event.data?.type === 'hosted-agent-cleanup-failed') {
          applyHostedAgentCleanupFailed(event.data.key, event.data.failure);
        } else if (event.data?.type === 'hosted-agent-cleanup-cleared') {
          applyHostedAgentCleanupCleared(event.data.key, event.data.at);
        } else if (event.data?.type === 'hosted-agent-cleanup-recreated') {
          applyHostedAgentCleanupRecreated(event.data.key, event.data.at);
        } else if (event.data?.type === 'hosted-agent-cleanup-snapshot-request') {
          applyHostedAgentCleanupSnapshot(event.data.records);
          deletionChannel?.postMessage({
            type: 'hosted-agent-cleanup-snapshot',
            requestId: event.data.requestId,
            records: getHostedAgentCleanupSnapshot(),
          } satisfies HostedAgentChannelMessage);
        } else if (
          event.data?.type === 'hosted-agent-cleanup-snapshot'
          && event.data.requestId === pendingSnapshotRequestId
        ) {
          // Keep accepting matching responses until this channel closes or a
          // newer request supersedes it. Every open tab may hold a distinct
          // record, and per-key timestamps make repeated merges idempotent.
          applyHostedAgentCleanupSnapshot(event.data.records);
          scheduleSynchronizationReady();
        }
      };
      pendingSnapshotRequestId = `${Date.now()}-${Math.random()}`;
      deletionChannel.postMessage({
        type: 'hosted-agent-cleanup-snapshot-request',
        requestId: pendingSnapshotRequestId,
        records: getHostedAgentCleanupSnapshot(),
      } satisfies HostedAgentChannelMessage);
      // BroadcastChannel has no way to report that no peer tabs exist. Give
      // existing tabs a brief opportunity to answer, then unblock this tab.
      scheduleSynchronizationReady();
    } catch {
      deletionChannel = null;
      pendingSnapshotRequestId = null;
      markSynchronizationReady();
    }
  };
  const closeChannel = () => {
    try { deletionChannel?.close(); } catch { /* ignore */ }
    deletionChannel = null;
    pendingSnapshotRequestId = null;
    if (snapshotReadyTimer) clearTimeout(snapshotReadyTimer);
    snapshotReadyTimer = null;
  };
  const onPageShow = (event: PageTransitionEvent) => {
    if (event.persisted) openChannel();
  };
  openChannel();
  window.addEventListener('pagehide', closeChannel);
  window.addEventListener('pageshow', onPageShow);
  import.meta.hot?.dispose(() => {
    window.removeEventListener('pagehide', closeChannel);
    window.removeEventListener('pageshow', onPageShow);
    closeChannel();
  });
}

/** Selector: the cleanup failure for a single app-scoped agent key, if any. */
export const useHostedAgentCleanupFailure = (
  appId: string | null | undefined,
  connectionKey: string | undefined,
): HostedAgentCleanupFailure | undefined =>
  useHostedAgentDeletionStore((state) =>
    connectionKey ? state.cleanupFailures[hostedAgentDeletionKey(appId, connectionKey)] : undefined,
  );
